// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import { endOfDay, format as formatDate, parseISO, startOfDay } from 'date-fns'
import jsonExport from 'jsonexport/dist'
import {
  AutocompleteInput,
  DateInput,
  downloadCSV,
  ExportButton,
  FilterButton,
  List,
  ReferenceInput,
  TextInput,
  TopToolbar,
} from 'react-admin'
import { formatGlucoseForExport } from '../common'
import ResourceListHeader from '../components/ResourceListHeader'
import { GLUCOSE_UNIT_STORAGE_KEY } from '../storeConfig'
import { neutralizeCsvRecord } from '../utils/csvFormula'
import { type GlucoseUnit } from '../utils/glucoseUnits'
import MetricDatagrid from './MetricDatagrid'

const toValidDate = (value: unknown): Date | undefined => {
  if (!value) return undefined
  // A bare "YYYY-MM-DD" from the date input must be read as a LOCAL date.
  // `new Date('2020-03-15')` is UTC midnight, which west of Greenwich is the
  // 14th at 17:00 — so startOfDay() then snapped the filter back a whole day
  // (gte "from the 15th" fetched from the 14th; lte "up to the 15th" ended at
  // 23:59 on the 14th, silently excluding a full day of readings).
  const date =
    value instanceof Date
      ? value
      : typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? parseISO(value)
        : new Date(value as string | number)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export const parseTimestampGte = (value: unknown): number | undefined => {
  const date = toValidDate(value)
  if (!date) return undefined
  return Math.floor(startOfDay(date).getTime() / 1000)
}

export const parseTimestampLte = (value: unknown): number | undefined => {
  const date = toValidDate(value)
  if (!date) return undefined
  return Math.floor(endOfDay(date).getTime() / 1000)
}

/**
 * These filters store Unix SECONDS, but react-admin's defaultFormat reads a
 * bare number as MILLISECONDS (`new Date(value)`, ra-ui-materialui
 * DateInput.tsx), so without this a filter set to 2020-03-15 displayed as
 * 1970-01-19.
 *
 * Must be IDEMPOTENT — react-admin applies format twice (useInput, then
 * DateInput's defaultValue) — hence the already-formatted pass-through. Reads
 * back in LOCAL time because parse used startOfDay/endOfDay in local time.
 */
export const formatTimestampFilter = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10)))
    return value.slice(0, 10)
  const secs = Number(value)
  if (!Number.isFinite(secs)) return ''
  const date = new Date(secs * 1000)
  return Number.isNaN(date.getTime()) ? '' : formatDate(date, 'yyyy-MM-dd')
}

const MetricListFilters = [
  // <SearchInput source="q" alwaysOn />,
  <DateInput
    key="timestamp_gte"
    label="pos.metrics.filters.timestamp_gte"
    source="timestamp_gte"
    format={formatTimestampFilter}
    parse={parseTimestampGte}
  />,
  <DateInput
    key="timestamp_lte"
    label="pos.metrics.filters.timestamp_lte"
    source="timestamp_lte"
    format={formatTimestampFilter}
    parse={parseTimestampLte}
  />,
  <TextInput key="glucose_gte" label="pos.metrics.filters.glucose_gte" source="glucose_gte" />,
  <TextInput key="glucose_lte" label="pos.metrics.filters.glucose_lte" source="glucose_lte" />,
  <ReferenceInput
    key="device"
    label="pos.metrics.filters.device"
    source="device"
    reference="devices"
    alwaysOn
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
]

/*
<ReferenceInput source="customer_id" reference="customers">
    <AutocompleteInput
        optionText={(choice?: Customer) =>
            choice?.id // the empty choice is { id: '' }
                ? `${choice.first_name} ${choice.last_name}`
                : ''
        }
        sx={{ minWidth: 200 }}
    />
</ReferenceInput>
*/

// Read the active display unit from the react-admin store's localStorage entry
// (the store is localStorage-backed; see storeConfig). The exporter is a plain
// function invoked on click, not a component, so it can't use the useStore hook
// — reading the key directly keeps MetricList hook-free while still honoring the
// portal's unit selector at export time.
const readActiveGlucoseUnit = (): GlucoseUnit => {
  try {
    const raw = localStorage.getItem(GLUCOSE_UNIT_STORAGE_KEY)
    return raw && JSON.parse(raw) === 'mg/dL' ? 'mg/dL' : 'mmol/L'
  } catch {
    return 'mmol/L'
  }
}

// The exported glucose column goes through `formatGlucoseForExport` (BGM
// correction + the same out-of-range masking the screen uses) rendered in the
// active display unit (mg/dL → integer, mmol/L → 1 decimal), so the CSV matches
// what the vet sees; the unit is also stated in the file name to avoid any
// ambiguity. Must stay identical to GlucoseCSVExport — two CSV export paths
// disagreeing about the same reading is the bug this shares a helper for.
// NOTE: cells can be "<40"/">540" strings, so this must NOT wrap them in
// Number() — that produced NaN for exactly the out-of-range rows that matter.
// P72-2: the raw record carries owner-writable text (notes, a populated
// device's name); jsonexport only quotes delimiters, so neutralize formulas.
export const prepareMetricsExportRecords = (records: any[], unit: string) =>
  records.map((record) => ({
    ...neutralizeCsvRecord(record),
    glucose:
      typeof record.glucose === 'number'
        ? formatGlucoseForExport(record.glucose, unit)
        : record.glucose,
  }))

const metricsExporter = (records: any[]) => {
  const unit = readActiveGlucoseUnit()
  const corrected = prepareMetricsExportRecords(records, unit)
  const unitSlug = unit === 'mg/dL' ? 'mg-per-dL' : 'mmol-per-L'
  jsonExport(corrected, (err: Error | null, csv: string) => downloadCSV(csv, `metrics-${unitSlug}`))
}

const ListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <ExportButton />
    </TopToolbar>
  )
}

const MetricList = () => (
  <>
    <ResourceListHeader resource="metrics" />
    <List
      perPage={50}
      actions={<ListActions />}
      filters={MetricListFilters}
      sort={{ field: 'timestamp', order: 'DESC' }}
      exporter={metricsExporter}
    >
      <MetricDatagrid rowClick="" />
    </List>
  </>
)

export default MetricList
